import React from "react"
import { useSelector } from "react-redux"
import { getCarrotLite } from "reducers/companyInfo"
import { Spacer } from "../atoms/Atoms"
import HowToUseYourBenefitCard from "./HowToUseYourBenefitCard"
import ChatWithExpertsCard from "./ChatWithExpertsCard"
import AvailableBenefitModule from "../molecules/AvailableBenefitModule"
import ProfileCompletionCard from "./ProfileCompletionCard"
import FindAProviderModule from "./FindAProviderModule"
import EligibilityModule from "./EligibilityModule"
import UngroupedReadAndLearnModule from "./UngroupedReadAndLearnModule"
import { getInterest, getJourney } from "../../../reducers/userInfo"
import {
  getHasInterestAndJourney,
  getHasPendingIvfRequirementsAndLikelyToDoIvf,
  getIsMemberOfCarrotCardCompany,
  getNeedsBenefitEnrollment
} from "derivedSelectors"
import Journeys from "utils/Journeys"
import { HomeHeader } from "../molecules/Molecules"
import { CarrotCardModuleContainer } from "components/views/molecules/CarrotCardModuleContainer"
import { MasterLayout } from "../atoms/layouts/MasterLayout"
import { BodyWithRightSidebarLayout } from "../atoms/layouts/BodyWithRightSidebarLayout"
import { ScrollToTop } from "../ScrollToTop"
import ReadAndLearnHero from "./ReadAndLearnHero"
import JourneyHero from "./JourneyHero"
import { SideBarBase } from "components/views/atoms/SidebarBase"
import { PromotionModules } from "../molecules/PromotionModules"
import Interests from "utils/Interests"
import ActionNeededForIvfHero from "./ActionNeededForIvfHero"
import { BenefitEnrollmentModule } from "components/views/molecules/BenefitEnrollmentModule"
import { useToggle } from "utils/Hooks"
import { FeatureGate } from "utils/FeatureGate"
import { BenefitEnrollmentFlowContainer } from "components/csd-enrollment"

export const HomeCard = ({ halfWidth, children }) => {
  const width = halfWidth ? "w-50" : "w-100"

  return (
    <div className={`${width} ph3 min-w250px flex-grow-1`}>
      {children}
      <div className="db dn-m dn-l">
        <Spacer height={3} />
      </div>
      <div className="dn db-ns">
        <Spacer height={2.25} />
      </div>
    </div>
  )
}

const HomeMain = () => {
  const { isCarrotLite, journey, hasInterestAndJourney, interest } = useSelector((state) => ({
    isCarrotLite: getCarrotLite(state),
    journey: getJourney(state),
    interest: getInterest(state),
    hasInterestAndJourney: getHasInterestAndJourney(state)
  }))

  // group 2a, 2b, 2c, 2d, 3a, 3b, 3c
  const showEligibilityModule =
    !isCarrotLite &&
    [Journeys.READY_FOR_NEXT_STEP, Journeys.TAKING_ACTION].includes(journey) &&
    interest !== Interests.TRYING_TO_GET_PREGNANT

  const showIvfRequirementsModule = useSelector(getHasPendingIvfRequirementsAndLikelyToDoIvf)
  return (
    <div className="flex flex-row flex-wrap">
      <FeatureGate filter={(c) => c.isLibraryEnabled}>
        {showIvfRequirementsModule ? (
          <ActionNeededForIvfHero />
        ) : hasInterestAndJourney ? (
          <ReadAndLearnHero />
        ) : (
          <JourneyHero />
        )}
        <div className="w-100">
          <Spacer height={3} />
        </div>
      </FeatureGate>
      <div className="nl3 nr3 flex-row flex-wrap flex">
        <HowToUseYourBenefitCard />
        <ChatWithExpertsCard />
      </div>
      <div className="nl3 nr3 flex-row flex-wrap flex">
        <ProfileCompletionCard halfWidth={showEligibilityModule || !hasInterestAndJourney} />
        {showEligibilityModule && <EligibilityModule />}
        <FeatureGate filter={(c) => c.isLibraryEnabled}>
          {!hasInterestAndJourney && <UngroupedReadAndLearnModule />}
        </FeatureGate>
      </div>
    </div>
  )
}

const HomeSidebar = () => {
  const needsBenefitEnrollment = useSelector(getNeedsBenefitEnrollment)
  const isCarrotLite = useSelector(getCarrotLite)
  const isMemberOfCarrotCardCompany = useSelector(getIsMemberOfCarrotCardCompany)
  const [showBenefitEnrollmentModal, toggleBenefitEnrollmentModal] = useToggle(false)

  return (
    <>
      <SideBarBase>
        {needsBenefitEnrollment && !isCarrotLite ? (
          <BenefitEnrollmentModule onClickEnroll={toggleBenefitEnrollmentModal} />
        ) : (
          !isCarrotLite && <AvailableBenefitModule id="home-sidebar-available-benefit-module" />
        )}
        {isMemberOfCarrotCardCompany && <CarrotCardModuleContainer backgroundColor="pampas" />}
        <PromotionModules />
        <FindAProviderModule />
      </SideBarBase>

      {showBenefitEnrollmentModal && <BenefitEnrollmentFlowContainer onExit={toggleBenefitEnrollmentModal} />}
    </>
  )
}

const Home = () => (
  // See https://trello.com/c/qQYnzDnt This was added because on mobile, home would load scrolled down a little.
  <ScrollToTop>
    <MasterLayout header={<HomeHeader />}>
      <BodyWithRightSidebarLayout sidebar={<HomeSidebar />}>
        <HomeMain />
      </BodyWithRightSidebarLayout>
    </MasterLayout>
  </ScrollToTop>
)

export default Home
